import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Select, SelectProps, Spin } from 'antd';
import { useAtomValue } from 'jotai';
import { artistsAtom } from './ddex_atoms';
import { fetchArtistSearchResults } from './artistSearchApi';
import ArtistSearchOption from './ArtistSearchOption';
import {
    duplicateArtistNameKeys,
    formatArtistOptionLabel,
    searchCatalogArtists,
} from './artistSearchUtils';

const { Option } = Select;

export type ArtistSelectProps = Omit<SelectProps, 'mode' | 'options' | 'filterOption' | 'children'> & {
    mode?: 'multiple' | 'tags';
    /** Credits: allow typing names not in catalog. */
    allowCustom?: boolean;
};

function catalogToSearchResult(
    artist: API.MusicDistribution.Artist,
    enriched?: API.MusicDistribution.ArtistSearchResult,
): API.MusicDistribution.ArtistSearchResult {
    return {
        key_name: artist.key_name,
        spotify_id: artist.spotify_id?.trim() || enriched?.spotify_id,
        apple_music_id: artist.apple_music_id?.trim() || enriched?.apple_music_id,
        last_release: enriched?.last_release ?? undefined,
    };
}

const ArtistSelect: React.FC<ArtistSelectProps> = ({
    allowCustom = false,
    mode,
    value,
    onChange,
    disabled,
    placeholder,
    dropdownRender,
    ...selectProps
}) => {
    const artistsQuery = useAtomValue(artistsAtom);
    const catalogArtists = artistsQuery.data ?? [];
    const catalogLoading = artistsQuery.isLoading;

    const duplicateKeys = useMemo(() => duplicateArtistNameKeys(catalogArtists), [catalogArtists]);
    const knownArtistKeys = useMemo(
        () => new Set(catalogArtists.map((a) => a.key_name)),
        [catalogArtists],
    );

    const [searchText, setSearchText] = useState('');
    const [enrichmentByKey, setEnrichmentByKey] = useState<
        Record<string, API.MusicDistribution.ArtistSearchResult>
    >({});
    const [apiSearching, setApiSearching] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const runEnrichmentSearch = useCallback((text: string) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        const trimmed = text.trim();
        if (trimmed.length < 2) {
            setApiSearching(false);
            return;
        }
        setApiSearching(true);
        debounceRef.current = setTimeout(() => {
            fetchArtistSearchResults(trimmed)
                .then((results) => {
                    setEnrichmentByKey((prev) => {
                        const next = { ...prev };
                        for (const row of results) {
                            next[row.key_name] = row;
                        }
                        return next;
                    });
                })
                .catch(() => {
                    /* API optional — client catalog search still works */
                })
                .finally(() => setApiSearching(false));
        }, 250);
    }, []);

    const selectedKeys = useMemo(() => {
        if (value == null) {
            return [];
        }
        const raw = Array.isArray(value) ? value : [value];
        return raw.map((v) => String(v).trim()).filter(Boolean);
    }, [value]);

    const matchedArtists = useMemo(() => {
        const q = searchText.trim();
        if (q.length < 2) {
            return [];
        }
        return searchCatalogArtists(catalogArtists, q, 20);
    }, [searchText, catalogArtists]);

    const optionKeys = useMemo(() => {
        const keys = new Set<string>();
        for (const artist of matchedArtists) {
            keys.add(artist.key_name);
        }
        for (const key of selectedKeys) {
            keys.add(key);
        }
        return Array.from(keys);
    }, [matchedArtists, selectedKeys]);

    const handleChange: NonNullable<SelectProps['onChange']> = (nextValue, option) => {
        if (allowCustom) {
            onChange?.(nextValue, option);
            return;
        }
        const values = (Array.isArray(nextValue) ? nextValue : [])
            .map((entry) => String(entry).trim())
            .filter(Boolean);
        onChange?.(values.filter((key) => knownArtistKeys.has(key)), option);
    };

    const resolvedMode = mode ?? (allowCustom ? 'tags' : 'multiple');

    const notFoundContent = catalogLoading || apiSearching ? (
        <Spin size="small" />
    ) : searchText.trim().length < 2 ? (
        'Type at least 2 characters to search'
    ) : allowCustom ? (
        'No match — type a name and press Enter'
    ) : (
        'No matching artists'
    );

    return (
        <Select
            {...selectProps}
            mode={resolvedMode}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            optionLabelProp="label"
            showSearch
            filterOption={false}
            onSearch={(text) => {
                setSearchText(text);
                runEnrichmentSearch(text);
            }}
            onDropdownVisibleChange={(open) => {
                if (!open) {
                    setSearchText('');
                }
            }}
            notFoundContent={notFoundContent}
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 520, ...(selectProps.dropdownStyle ?? {}) }}
            dropdownRender={dropdownRender}
            placeholder={
                placeholder ??
                (allowCustom ? 'Select or type a name' : 'Search artists (min. 2 characters)')
            }
        >
            {optionKeys.map((key) => {
                const fromCatalog = catalogArtists.find((a) => a.key_name === key);
                const enriched = enrichmentByKey[key];
                const searchResult: API.MusicDistribution.ArtistSearchResult = fromCatalog
                    ? catalogToSearchResult(fromCatalog, enriched)
                    : enriched ?? { key_name: key };

                const label = fromCatalog
                    ? formatArtistOptionLabel(fromCatalog, duplicateKeys)
                    : key;

                return (
                    <Option key={key} value={key} label={label}>
                        <ArtistSearchOption artist={searchResult} />
                    </Option>
                );
            })}
        </Select>
    );
};

export default ArtistSelect;
